import { useEffect, useRef, useState } from 'react';
import './sheet.css';
import './recurring-delete.css';

export type RecurringDeleteScope = 'occurrence' | 'all';

/**
 * Google-Calendar-style prompt shown when deleting a recurring item: skip just
 * this meeting's occurrence, or remove the item from all future agendas.
 */
export function RecurringDeleteDialog({
  open,
  title,
  onConfirm,
  onClose,
}: {
  open: boolean;
  title?: string;
  onConfirm: (scope: RecurringDeleteScope) => void;
  onClose: () => void;
}) {
  const ref = useRef<HTMLDialogElement>(null);
  const [scope, setScope] = useState<RecurringDeleteScope>('occurrence');

  useEffect(() => {
    const dlg = ref.current;
    if (!dlg) return;
    if (open && !dlg.open) {
      setScope('occurrence');
      dlg.showModal();
    } else if (!open && dlg.open) {
      dlg.close();
    }
  }, [open]);

  const onClick = (e: React.MouseEvent<HTMLDialogElement>) => {
    if (e.target === ref.current) onClose();
  };

  return (
    <dialog ref={ref} className="confirm" onClose={onClose} onClick={onClick}>
      <div className="confirm-panel">
        <h2 className="confirm-title">Delete recurring item</h2>
        {title ? <p className="confirm-message">“{title}” repeats across meetings.</p> : null}

        <div className="recur-del-options">
          <label className="recur-del-option">
            <input
              type="radio"
              name="recur-del-scope"
              checked={scope === 'occurrence'}
              onChange={() => setScope('occurrence')}
            />
            <span className="recur-del-text">
              This occurrence
              <small>Skip it for this meeting only; it returns next time.</small>
            </span>
          </label>
          <label className="recur-del-option">
            <input
              type="radio"
              name="recur-del-scope"
              checked={scope === 'all'}
              onChange={() => setScope('all')}
            />
            <span className="recur-del-text">
              All future occurrences
              <small>Remove the item so it stops carrying forward.</small>
            </span>
          </label>
        </div>

        <div className="confirm-actions">
          <button className="btn btn-ghost" onClick={onClose}>
            Cancel
          </button>
          <button
            className="btn btn-danger"
            onClick={() => {
              onClose();
              onConfirm(scope);
            }}
          >
            {scope === 'all' ? 'Delete' : 'Skip'}
          </button>
        </div>
      </div>
    </dialog>
  );
}
